import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { AcDialog, Button, EmptyState, Icon, Spinner } from "@cntrl/ui";
import { useBandContext } from "../../app/bandContext.js";
import { useLiveGroup } from "../../app/realtime/RealtimeProvider.js";
import { useRoadCases, useSetCaseState, useResetCases } from "../../app/hooks/useRoadCases.js";
import { ApiError } from "../../app/hooks/apiError.js";
import { CASE_STATE_LABEL, asCaseState, caseRollup, itemCountLine, nextCaseState, sortCases } from "./caseStates.js";
import { CaseSheet } from "./CaseSheet.js";
import { ScanModeBanner, ScanTargetPicker, useCaseScanMode } from "./ScanMode.js";
import styles from "./roadCases.module.scss";

/**
 * The CASE CHECKLIST (ART-253, legacy `ProdCasesCard` parity) — the band's road cases and where each one is
 * (Home · In venue · On truck), live across the crew via `road-cases-changed`. One component for both homes: the
 * mobile home card and the `/road-cases` page render this body, so they cannot disagree.
 *
 *  - The roll-up line says where everything is ("2 in venue · 3 on truck of 6").
 *  - A row's pill CYCLES the case on a manual tap; the row opens the case sheet (items, sticker, delete).
 *  - Scan starts iOS scan mode toward a chosen target; Reset sends everything home (confirmed first).
 *
 * Rows sort what still has to move to the top. Nothing is optimistic: the pill shows the state the server holds.
 */
export function CaseChecklist() {
  const { bandId } = useBandContext();
  useLiveGroup(`band:${bandId}`);
  const q = useRoadCases();
  const setState = useSetCaseState();
  const reset = useResetCases();
  const scanMode = useCaseScanMode();
  const [openCase, setOpenCase] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [adding, setAdding] = useState(false);

  if (q.isLoading) {
    return <div className={styles.center} role="status" aria-live="polite"><Spinner label="Loading cases" /></div>;
  }
  if (q.isError) {
    return (
      <EmptyState
        title="Couldn't load cases"
        description={q.error instanceof ApiError ? q.error.message : "Check your connection and try again."}
        action={<Button onClick={() => void q.refetch()}>Retry</Button>}
      />
    );
  }

  const cases = sortCases(q.data?.items ?? []);

  return (
    <div className={styles.checklist}>
      <div className={styles.checklistHead}>
        <p className={styles.rollup}>{caseRollup(cases)}</p>
        {cases.length > 0 && (
          <div className={styles.headActions}>
            <button
              type="button"
              className={styles.scanBtn}
              disabled={scanMode.active != null}
              onClick={() => setPicking(true)}
            >
              Scan
            </button>
            <button type="button" className={styles.resetBtn} onClick={() => setConfirmReset(true)}>
              Reset
            </button>
          </div>
        )}
      </div>

      {scanMode.active && <ScanModeBanner state={scanMode.active} onStop={scanMode.stop} />}

      {cases.length > 0 && (
        <ul className={styles.rows} data-scroll={cases.length > 5 || undefined}>
          {cases.map((c) => {
            const id = c.caseId;
            if (!id) return null;
            const st = asCaseState(c.state);
            return (
              <li key={id} className={styles.row}>
                <button
                  type="button"
                  className={styles.pill}
                  data-state={st}
                  aria-label={`${c.name}: ${CASE_STATE_LABEL[st]}. Tap to move to ${CASE_STATE_LABEL[nextCaseState(st)]}`}
                  disabled={setState.isPending}
                  onClick={() => setState.mutate({ caseId: id, state: nextCaseState(st) })}
                >
                  {CASE_STATE_LABEL[st]}
                </button>
                <button type="button" className={styles.rowBody} aria-label={`Open ${c.name}`} onClick={() => setOpenCase(id)}>
                  <span className={styles.rowName}>{c.name}</span>
                  <span className={styles.rowMeta}>{itemCountLine(c.itemCount)}</span>
                  <Icon icon={ChevronRight} size={16} className={styles.rowChevron} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* One popup for a new case — name, contents and sticker together (PO, ART-253). */}
      <button type="button" className={styles.addCase} onClick={() => setAdding(true)}>
        {cases.length === 0 ? "+ Add your first case" : "+ Add case"}
      </button>

      {openCase && <CaseSheet caseId={openCase} onClose={() => setOpenCase(null)} />}
      {adding && <CaseSheet caseId={null} onClose={() => setAdding(false)} />}
      {picking && (
        <ScanTargetPicker
          onClose={() => setPicking(false)}
          onPick={(target) => {
            setPicking(false);
            void scanMode.start(target);
          }}
        />
      )}
      {confirmReset && (
        <AcDialog
          title="Reset all cases?"
          message="Every case goes back to Home."
          confirmLabel="Reset"
          loading={reset.isPending}
          onCancel={() => setConfirmReset(false)}
          onConfirm={() => reset.mutate(undefined, { onSuccess: () => setConfirmReset(false) })}
        />
      )}
    </div>
  );
}
